// Import action types.
import ActionTypes 				from './actionTypes.js';

// Selectors.
import Selectors 				from './selectors.js';

// Config.
import Config					from './config/config.js';

// Model business logic.
import { Logic } 				from './model.js';

// Legacy dependencies.
import { requireSDK } 			from '../../../legacy/utils/utils.js';

/**
 * <pre>
 * Overlay module Actions.
 *
 * Summary of responsibilities:
 *
 *  - implements Redux action creators specific to component
 *
 * FYI:
 *
 * We are using {@link https://github.com/gaearon/redux-thunk|Redux Thunk} middleware on our {@link https://github.com/reactjs/redux|Redux} store.
 * For this reason you will see that inside each action creator, we return a function, rather than an action object directly.
 * </pre>
 *
 * @see {@link https://github.com/gaearon/redux-thunk|Redux Thunk Documentation}
 * @see {@link https://github.com/reactjs/redux|Redux Documentation}
 *
 * @author Tim Loram

 * @memberof module:Overlay
 * @namespace
 *
 * @type {Object}
 */
const Actions = {

	/**
	 * Open overlay
	 *
	 */
	open: function(instanceId) {

		return function(dispatch) {

			// Dispatch action to register overlay instance.
			dispatch({
				type: 		ActionTypes.REGISTER_INSTANCE,
				id: 		instanceId
			});

			// Dispatch action to make overlay hidden initially (will be made visible after centering has taken place).
			dispatch({
				type: 		ActionTypes.UPDATE_VISIBILITY,
				id: 		instanceId,
				visible: 	false
			});

			// Dispatch action to mark overlay as open.
			dispatch({
				type: 		ActionTypes.OPEN,
				id: 		instanceId
			});

			// - - - - - - - - - - - - - - - - - -
			// Get Event service.
			let eventService = NGXD.getServiceContainer().getService('event');

			// Trigger event.
			eventService.trigger('overlay:ready');
		}
	},

	/**
	 * Close overlay
	 *
	 */
	close: function(instanceId) {

		return function(dispatch) {

			// Dispatch action to make overlay hidden.
			dispatch({
				type: 		ActionTypes.UPDATE_VISIBILITY,
				id: 		instanceId,
				visible: 	false
			});

			// Dispatch action to mark overlay as closed.
			dispatch({
				type: 		ActionTypes.CLOSE,
				id: 		instanceId
			});

      Logic.Position.resetMinHeight();

			// - - - - - - - - - - - - - - - - - -
			// Get Event service.
			let eventService = NGXD.getServiceContainer().getService('event');

			// Trigger event.
			eventService.trigger('overlay:destroyed');
		}
	},

	/**
	 * Center overlay
	 *
	 */
	center: function(instanceId, overlayInner) {

		return function(dispatch) {

			// Get Config service.
			let configService = NGXD.getServiceContainer().getService('config');

			// Get State service.
			let stateService = NGXD.getServiceContainer().getService('state');

			// Get Api service.
			let apiService = NGXD.getServiceContainer().getService('api');

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// Function to dispatch actual center actions once all logic has been executed.
			let _center = function(top) {

				// Dispatch action to center overlay.
				dispatch({
					type: 	ActionTypes.CENTER,
					id: 	instanceId,
					top: 	top
				});

				// Dispatch action to make overlay visible.
				dispatch({
					type: 		ActionTypes.UPDATE_VISIBILITY,
					id: 		instanceId,
					visible: 	true
				});
			}

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// If we are in Facebook.
			if (configService.get('social.fb.isFb') && stateService.get('window.framed')) {

				// Dispatch action to set overlay positioning mode.
				dispatch({
					type: 			ActionTypes.UPDATE_POSITION_MODE,
					id: 			instanceId,
					positionMode: 	'xPositionFrame'
				});

				// Load Facebook SDK if required.
				requireSDK((log.getDebugLevel()?'fbdebug':'fb'),function() {

					FB.Canvas.getPageInfo(function(info) {

						// Calculate vertical center for overlay.
			            let top = Logic.Position.calculateVerticalCenter(overlayInner, info);

			            // Call function to dispatch center actions.
			            _center(top);
					});
				});

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
			// If we are in a normal website embed (and more importantly the Embed API has been loaded in the parent window).
			} else if (stateService.get('window.embedApiAvailable')) {

				// Dispatch action to set overlay positioning mode.
				dispatch({
					type: 			ActionTypes.UPDATE_POSITION_MODE,
					id: 			instanceId,
					positionMode: 	'xPositionFrame'
				});

				apiService.execute('messageParent', {action:'getpageinfo'}, function(info) {

	                // Calculate vertical center for overlay.
		            let top = Logic.Position.calculateVerticalCenter(overlayInner, info);

		            // Call function to dispatch center actions.
			        _center(top);
	            });

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
	        // If we are not embedded at all.
	        } else {

	        	// Dispatch action to set overlay positioning mode.
				dispatch({
					type: 			ActionTypes.UPDATE_POSITION_MODE,
					id: 			instanceId,
					positionMode: 	'xPositionScreen'
				});

				let info;

		        // If no pageInfo state property already set.
		        if(!stateService.get('window.pageInfo')) {

		        	// Calculate page info.
					info = Logic.Position.calculatePageInfo();

		        // Otherwise, use existing info.
		        } else {

		        	info = stateService.get('window.pageInfo');
		        }

		        // Calculate vertical center for overlay.
	            let top = Logic.Position.calculateVerticalCenter(overlayInner, info);

		        // Call function to dispatch center actions.
			    _center(top);
			}
		}
	}
};

export default Actions;
